import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, Loader2 } from "lucide-react";
import saApi, { SA_API } from "../superadminApi";
import { autoDescription, firstImageSrc } from "../seo";
import { pageDescription } from "../seoDefaults";

const inputCls = "w-full border border-gray-200 rounded-lg px-3 py-2 text-[14px] focus:border-[#16345c] outline-none";
const mediaSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${SA_API.replace(/\/api$/, "")}${u}`);

const Counter = ({ value, max, testid }) => {
  const n = (value || "").length;
  return <span data-testid={testid} className={`text-[11px] font-semibold ${n > max ? "text-red-500" : "text-gray-400"}`}>{n}/{max}</span>;
};

// Immagine social (og:image) 1200×630: il backend ritaglia/adatta automaticamente.
const OgImageField = ({ page, onChange }) => {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try {
      const r = await saApi.post("/superadmin/pages/og-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange({ og_image: r.data.url });
      toast.success("Immagine social adattata a 1200×630");
    } catch (err) { toast.error(err?.response?.data?.detail || "Caricamento non riuscito"); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const fallback = firstImageSrc(page.content);
  return (
    <div className="mt-3" data-testid="sa-page-og-image">
      <span className="block text-[12px] font-semibold text-gray-600 mb-1">Immagine social (og:image · 1200×630)</span>
      <div className="flex items-start gap-3">
        <div className="w-[160px] aspect-[1200/630] rounded-lg border border-gray-200 bg-white overflow-hidden shrink-0">
          {page.og_image
            ? <img src={mediaSrc(page.og_image)} alt="Anteprima immagine social" className="w-full h-full object-cover" data-testid="sa-page-og-preview" />
            : <div className="w-full h-full flex items-center justify-center text-[10.5px] text-gray-400 text-center px-2">{fallback ? "Usa la prima immagine del contenuto" : "Usa l'immagine predefinita del sito"}</div>}
        </div>
        <div className="flex flex-col gap-1.5">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="sa-page-og-upload"
            className="inline-flex items-center gap-1.5 border border-gray-200 hover:border-[#16345c] rounded-lg px-3 py-1.5 text-[12.5px] font-semibold text-[#16345c] disabled:opacity-60">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Carica immagine
          </button>
          {page.og_image && (
            <button type="button" onClick={() => onChange({ og_image: "" })} data-testid="sa-page-og-remove"
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-red-500 hover:text-red-600"><Trash2 size={13} /> Rimuovi</button>
          )}
          <span className="text-[11px] text-gray-400 max-w-[220px]">Formati diversi vengono ritagliati al centro. Se vuoto: prima immagine del contenuto, poi immagine predefinita.</span>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" onChange={upload} className="hidden" data-testid="sa-page-og-input" />
    </div>
  );
};

// Campi SEO della pagina CMS: title, meta description, og:image e noindex (vuoti = fallback automatico).
const PageSeoFields = ({ page, onChange }) => {
  const fallbackTitle = `${page.title || "Titolo"} — JOBTIMEHR`;
  const fallbackDesc = pageDescription(page.slug) || autoDescription(page.content) || "Generata automaticamente dal primo paragrafo (max 155 caratteri).";
  return (
    <div className="mb-4 rounded-xl border border-gray-200 bg-gray-50 p-4" data-testid="sa-page-seo">
      <div className="text-[12px] font-black text-[#16345c] uppercase tracking-wide mb-3">SEO</div>
      <label className="block mb-3">
        <span className="flex items-center justify-between text-[12px] font-semibold text-gray-600 mb-1">
          SEO Title <Counter value={page.seo_title} max={60} testid="sa-page-seo-title-count" />
        </span>
        <input value={page.seo_title || ""} data-testid="sa-page-seo-title" placeholder={fallbackTitle}
          onChange={(e) => onChange({ seo_title: e.target.value })} className={inputCls} />
      </label>
      <label className="block">
        <span className="flex items-center justify-between text-[12px] font-semibold text-gray-600 mb-1">
          Meta Description <Counter value={page.meta_description} max={155} testid="sa-page-meta-description-count" />
        </span>
        <textarea rows={3} value={page.meta_description || ""} data-testid="sa-page-meta-description" placeholder={fallbackDesc}
          onChange={(e) => onChange({ meta_description: e.target.value })} className={`${inputCls} resize-y`} />
      </label>
      <p className="text-[11px] text-gray-400 mt-1">Se lasciati vuoti si usano il titolo della pagina e la descrizione predefinita (o la prima frase compiuta del contenuto, max 155 caratteri).</p>
      <OgImageField page={page} onChange={onChange} />
      <label className="mt-3 flex items-center gap-2 text-[13px] font-semibold text-gray-700 cursor-pointer">
        <input type="checkbox" checked={!!page.noindex} data-testid="sa-page-noindex" onChange={(e) => onChange({ noindex: e.target.checked })} className="w-4 h-4 accent-[#ff7e00]" />
        Escludi dai motori di ricerca (noindex, esclusa dalla sitemap)
      </label>
    </div>
  );
};

export default PageSeoFields;
